/**
 * TopTicker - the public site's live-data strip, pinned at the very top of the
 * page ABOVE the header (NCX-style). Blends world commodity prices ($, per
 * tonne), the NGN/USD close, and the sharpest local market moves. Renders
 * nothing until data arrives; feeds that are not connected simply don't
 * appear, which is why the loss of the exchange feed went unnoticed here:
 * the strip did not break, it just got shorter.
 */

import React from 'react';
import MarketTicker, { movementsToTicker } from './MarketTicker';
import { usePriceMovements, useGlobalCommodities, useGlobalPrices, useFxSeries, useCommodities, useCategories, useWorldSeriesCatalogue, useWorldReferenceBatch } from '../../services/queries';
import { isCommodityActive } from '../../utils/catalogStatus';
import {
  WORLD_CARD_SERIES, toPriceMap, toPickerOptions, exchangeFeedIsEmpty,
  cardLabel, kgPerPublishedUnit,
} from '../../utils/worldReference';

function TopTicker() {
  const pm = usePriceMovements();
  const gc = useGlobalCommodities();
  const gp = useGlobalPrices('ALL');
  const fx = useFxSeries('ALL');
  const com = useCommodities();
  const cats = useCategories();

  // The exchange feed expired, so the strip quietly lost its world prices
  // altogether: no crash, no message, just three fewer things scrolling past.
  // It falls back to the same World Bank series the rest of the site uses.
  const usingWorldReference = exchangeFeedIsEmpty(gp.data || {});
  const cat = useWorldSeriesCatalogue({ enabled: usingWorldReference });
  const worldOptions = toPickerOptions(cat.data);
  const worldBatch = useWorldReferenceBatch(
    worldOptions.map((o) => o.id),
    120,
    { enabled: usingWorldReference && worldOptions.length > 0 },
  );
  const worldPrices = toPriceMap(worldBatch.data);

  const items = [];

  if (usingWorldReference) {
    // Per tonne, the familiar magnitude, matching the cards elsewhere. A
    // ticker has no room to explain a unit, so it must not show cocoa at
    // $5.95 beside maize at $224 and leave the reader to work out why.
    const byId = Object.fromEntries(worldOptions.map((o) => [o.id, o]));
    WORLD_CARD_SERIES.filter((id) => byId[id]).forEach((id) => {
      const o = byId[id];
      const kg = kgPerPublishedUnit(o.unit);
      const rows = (worldPrices[id] || []).filter((r) => r.close != null);
      const latest = rows[rows.length - 1];
      const prev = rows[rows.length - 2];
      if (!latest || !prev || !prev.close) return;
      const toTonne = (v) => (kg ? (v / kg) * 1000 : v);
      const { name } = cardLabel(o.label, o.group);
      items.push({
        label: name,
        sub: 'World Bank',
        value: `$${Math.round(toTonne(latest.close)).toLocaleString()}`,
        change: ((latest.close - prev.close) / prev.close) * 100,
      });
    });
  }

  // Global futures - "Cocoa · ICE  $9,800 ▲ 1.9%"
  const globals = usingWorldReference ? [] : (gc.data || []);
  const prices = gp.data || {};
  globals.forEach((g) => {
    const s = prices[g.id] || [];
    // The last two rows that carry a figure, not simply the last two rows.
    // open/high/low/close are all nullable and a trailing row with no close
    // is ordinary. The guard below checked prev.close but never latest.close,
    // so a null there reached toLocaleString and threw, and because this strip
    // sits in PublicLayout it took down every public page with it.
    const priced = s.filter((r) => r.close != null);
    const latest = priced[priced.length - 1];
    const prev = priced[priced.length - 2];
    if (!latest || !prev || !prev.close) return;
    items.push({
      label: g.name,
      sub: g.exchange,
      value: `$${latest.close.toLocaleString()}`,
      change: ((latest.close - prev.close) / prev.close) * 100,
    });
  });

  // NGN/USD weekly close
  // Same treatment for the exchange rate: a row with no rate is skipped
  // rather than trusted to be a number.
  const rates = (fx.data || []).filter((r) => r.rate != null);
  const latestRate = rates[rates.length - 1];
  const prevRate = rates[rates.length - 2];
  if (latestRate && prevRate && prevRate.rate) {
    items.push({
      label: 'NGN/USD',
      sub: 'CBN',
      value: `₦${latestRate.rate.toLocaleString()}`,
      change: ((latestRate.rate - prevRate.rate) / prevRate.rate) * 100,
    });
  }

  // Local movers (largest first, zero-change dropped). RETIRED commodities
  // (inactive commodity or category) stay out of the live strip.
  const byName = new Map((com.data || []).map((x) => [x.name, x]));
  const liveMovements = (pm.data || []).filter((m) => {
    const c = byName.get(m.topCommodity);
    return !c || isCommodityActive(c, cats.data || []);
  });
  items.push(...movementsToTicker(liveMovements));

  if (items.length === 0) return null;
  return <MarketTicker live items={items} variant="bar" />;
}

export default TopTicker;
